import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Empty, Form, Input, InputNumber, List, Select, Space, Spin, Switch, Tag, Typography, message } from 'antd'
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons'
import { leadsApi, type SourceTemplate } from '../services/leadsApi'
import { crmIntegrationApi } from '../services/crmIntegrationApi'
import FirstMessageRules from './FirstMessageRules'
import { accountsApi } from '../services/api'

const { Text, Title } = Typography

function extractError(error: unknown): string {
    if (typeof error === 'object' && error && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string } } }).response
        return response?.data?.detail || 'Ошибка запроса'
    }
    return 'Ошибка запроса'
}

function firstMessageSettingsPayload(values: Record<string, any>) {
    return {
        enabled: values.enabled !== false,
        strategy: values.strategy || 'random',
        selected_account_id: values.selected_account_id || null,
        selected_account_ids: values.selected_account_ids || [],
        min_delay_seconds: values.min_delay_seconds || 0,
        max_delay_seconds: values.max_delay_seconds || 0,
        global_throttle_enabled: Boolean(values.global_throttle_enabled),
        global_min_delay_seconds: values.global_min_delay_seconds ?? 60,
        global_max_delay_seconds: values.global_max_delay_seconds ?? 180,
        rotate_after_messages: values.rotate_after_messages || 1,
        per_account_daily_limit: values.per_account_daily_limit || null,
        max_send_attempts: values.max_send_attempts || 3,
        retry_interval_minutes: values.retry_interval_minutes || 120,
        require_geo_pool: values.require_geo_pool !== false,
        require_proxy_geo: values.require_proxy_geo !== false,
        respect_account_health: true,
        phone_fallback_enabled: Boolean(values.phone_fallback_enabled),
    }
}

export default function BroadcastSettings() {
    const [templates, setTemplates] = useState<SourceTemplate[]>([])
    const [templatesLoading, setTemplatesLoading] = useState(false)
    const [refreshingSources, setRefreshingSources] = useState(false)
    const [accounts, setAccounts] = useState<any[]>([])
    const [settingsForm] = Form.useForm()
    const [settingsLoading, setSettingsLoading] = useState(false)
    const [settingsSaving, setSettingsSaving] = useState(false)
    const strategy = Form.useWatch('strategy', settingsForm)
    const globalThrottle = Form.useWatch('global_throttle_enabled', settingsForm)

    const accountOptions = useMemo(() => accounts.map((account) => ({
        value: account.id,
        label: `${account.phone}${account.username ? ` · @${account.username}` : ''}${account.pool_geo ? ` · ${account.pool_geo}` : ''}${account.is_connected ? ' · подключен' : ''}`,
    })), [accounts])

    const loadTemplates = useCallback(async () => {
        setTemplatesLoading(true)
        try {
            const response = await leadsApi.getSourceTemplates()
            setTemplates(response.data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setTemplatesLoading(false)
        }
    }, [])

    const loadSettings = useCallback(async () => {
        setSettingsLoading(true)
        try {
            const [settingsResponse, accountsResponse] = await Promise.all([
                accountsApi.getFirstMessageSettings(),
                accountsApi.getAll(0, 500, true),
            ])
            settingsForm.setFieldsValue({
                ...settingsResponse.data,
                selected_account_ids: settingsResponse.data.selected_account_ids || [],
            })
            setAccounts(accountsResponse.data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSettingsLoading(false)
        }
    }, [settingsForm])

    useEffect(() => {
        loadTemplates()
        loadSettings()
    }, [loadTemplates, loadSettings])

    // Тот же справочник, что и в разделе интеграции: тянем актуальный список
    // источников из CRM Wiseway и сразу показываем, у каких текст ещё не задан.
    const refreshSources = async () => {
        setRefreshingSources(true)
        try {
            const { data } = await crmIntegrationApi.refreshSources()
            await loadTemplates()
            message.success(`Источники обновлены: ${data.count}`)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setRefreshingSources(false)
        }
    }

    const saveSettings = async (changes?: Record<string, any>) => {
        setSettingsSaving(true)
        try {
            const values = { ...settingsForm.getFieldsValue(true), ...(changes || {}) }
            if (changes) settingsForm.setFieldsValue(changes)
            await accountsApi.updateFirstMessageSettings(firstMessageSettingsPayload(values))
            message.success('Настройки сохранены')
        } catch (error) {
            message.error(extractError(error))
            await loadSettings()
        } finally {
            setSettingsSaving(false)
        }
    }

    return (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <FirstMessageRules sources={templates} />

            <div>
                <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 0 }} align="center">
                    <Title level={5} style={{ margin: 0 }}>Источники лидов</Title>
                    <Button icon={<ReloadOutlined />} loading={refreshingSources} onClick={refreshSources}>
                        Обновить источники
                    </Button>
                </Space>
                <Alert
                    type="info"
                    showIcon
                    style={{ margin: '14px 0' }}
                    message="Справочник источников из CRM Wiseway. Тексты задаются правилами выше — здесь видно, каким правилом покрыт каждый источник."
                />

                <Spin spinning={templatesLoading}>
                    <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                        <List
                            dataSource={templates}
                            locale={{ emptyText: <Empty description="Источники не найдены — нажмите «Обновить источники»" /> }}
                            renderItem={(item) => (
                                <List.Item style={{ padding: '10px 12px' }}>
                                    <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
                                        <Space direction="vertical" size={2}>
                                            <Text>{item.source}</Text>
                                            {!item.is_known && <Text type="secondary" style={{ fontSize: 12 }}>нет в справочнике CRM</Text>}
                                            {item.is_known && !item.source_enabled && <Text type="secondary" style={{ fontSize: 12 }}>лиды не загружаются</Text>}
                                        </Space>
                                        {item.rule_name
                                            ? (
                                                <Tag color={item.rule_partial ? 'gold' : 'green'}>
                                                    {item.rule_partial
                                                        ? `правило «${item.rule_name}» — нужны ещё условия`
                                                        : `правило «${item.rule_name}»`}
                                                </Tag>
                                            )
                                            : <Tag color="red">не покрыт правилом</Tag>}
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </div>
                </Spin>
            </div>

            <div>
                <Title level={5}>Общие настройки рассылки</Title>
                <Text type="secondary">Действуют для всех источников. Раньше находились в разделе «Очередь рассылок».</Text>
                <Spin spinning={settingsLoading}>
                    <Form form={settingsForm} layout="vertical" style={{ marginTop: 14 }}>
                        <Space size={16} wrap>
                            <Form.Item name="enabled" label="Автоотправка" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Switch disabled={settingsSaving} onChange={(checked) => saveSettings({ enabled: checked })} />
                            </Form.Item>
                            <Form.Item name="require_geo_pool" label="Только гео-пул лида" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Switch disabled={settingsSaving} onChange={(checked) => saveSettings({ require_geo_pool: checked })} />
                            </Form.Item>
                            <Form.Item name="require_proxy_geo" label="Только прокси гео TG-аккаунта" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Switch disabled={settingsSaving} onChange={(checked) => saveSettings({ require_proxy_geo: checked })} />
                            </Form.Item>
                        </Space>

                        <Form.Item name="strategy" label="Как выбирать аккаунт" style={{ marginTop: 16 }}>
                            <Select
                                options={[
                                    { value: 'random', label: 'Случайный аккаунт из подходящего пула' },
                                    { value: 'round_robin', label: 'По кругу между аккаунтами' },
                                    { value: 'single', label: 'Только один выбранный аккаунт' },
                                    { value: 'selected', label: 'Только выбранные аккаунты' },
                                ]}
                            />
                        </Form.Item>
                        {strategy === 'single' && (
                            <Form.Item name="selected_account_id" label="Аккаунт для отправки">
                                <Select showSearch optionFilterProp="label" options={accountOptions} />
                            </Form.Item>
                        )}
                        {strategy === 'selected' && (
                            <Form.Item name="selected_account_ids" label="Выбранные аккаунты">
                                <Select mode="multiple" showSearch optionFilterProp="label" options={accountOptions} />
                            </Form.Item>
                        )}

                        <Space size={12} wrap>
                            <Form.Item name="max_send_attempts" label="Попыток на лида" tooltip="Сколько раз пытаться достучаться до лида. Попытка тратится только при реальном обращении к Telegram.">
                                <InputNumber min={1} max={20} />
                            </Form.Item>
                            <Form.Item name="retry_interval_minutes" label="Пауза между попытками, мин" tooltip="Без паузы все попытки израсходуются за считанные минуты.">
                                <InputNumber min={1} max={10080} />
                            </Form.Item>
                            <Form.Item
                                name="min_delay_seconds"
                                label="Мин. задержка аккаунта, сек"
                                tooltip="После отправки первого сообщения TG-аккаунт «отдыхает» случайное время от мин до макс и новых лидов не получает. Остальные аккаунты пула в это время работают. 0 и 0 — без отдыха."
                            >
                                <InputNumber min={0} max={86400} />
                            </Form.Item>
                            <Form.Item
                                name="max_delay_seconds"
                                label="Макс. задержка аккаунта, сек"
                                tooltip="Верхняя граница отдыха аккаунта. Пауза каждый раз выбирается случайно между мин и макс, чтобы аккаунт писал не по таймеру."
                            >
                                <InputNumber min={0} max={86400} />
                            </Form.Item>
                            <Form.Item
                                name="rotate_after_messages"
                                label="Менять после сообщений"
                                tooltip="Работает только при способе «По кругу между аккаунтами»: сколько первых сообщений подряд отправляет один аккаунт, прежде чем очередь перейдёт к следующему. При других способах выбора не используется."
                            >
                                <InputNumber min={1} max={10000} disabled={strategy !== 'round_robin'} />
                            </Form.Item>
                            <Form.Item name="per_account_daily_limit" label="Лимит в день на аккаунт">
                                <InputNumber min={1} max={100000} placeholder="Без лимита" />
                            </Form.Item>
                        </Space>

                        <Space size={16} wrap align="start" style={{ marginTop: 4 }}>
                            <Form.Item
                                name="global_throttle_enabled"
                                label="Отправлять по одному с общей задержкой"
                                valuePropName="checked"
                                tooltip="Первые сообщения уходят строго по одному на весь Оркестратор: следующее — только через случайную паузу от мин до макс, каким бы аккаунтом оно ни отправлялось. Действует вместе с задержкой аккаунта: сообщение уйдёт, когда закончилась общая пауза, и только с отдохнувшего аккаунта."
                            >
                                <Switch />
                            </Form.Item>
                            {globalThrottle && (
                                <>
                                    <Form.Item name="global_min_delay_seconds" label="Общая задержка, мин, сек">
                                        <InputNumber min={0} max={86400} />
                                    </Form.Item>
                                    <Form.Item name="global_max_delay_seconds" label="Общая задержка, макс, сек">
                                        <InputNumber min={0} max={86400} />
                                    </Form.Item>
                                </>
                            )}
                        </Space>

                        <Space size={16} wrap align="start" style={{ marginTop: 4 }}>
                            <Form.Item
                                name="phone_fallback_enabled"
                                label="Писать по телефону, если ника нет или он не сработал"
                                valuePropName="checked"
                                tooltip="Сначала всегда отправка по нику. Только если Telegram ответил, что такого ника нет, или ника у лида нет вовсе, а телефон заполнен, тот же аккаунт добавляет номер в контакты, пишет по нему и сразу удаляет контакт. Если по номеру никого нет или он скрыт настройками приватности — лид уходит в «Ошибки» с пометкой «по телефону не найден». Неудача по телефону аккаунт из рассылки не выводит."
                            >
                                <Switch />
                            </Form.Item>
                        </Space>

                        <Button type="primary" icon={<SaveOutlined />} loading={settingsSaving} onClick={() => saveSettings()}>
                            Сохранить настройки
                        </Button>
                    </Form>
                </Spin>
            </div>
        </Space>
    )
}
